import React, { useState } from 'react';
import styled from 'styled-components';
import ClubSearchSelect from './ClubSearchSelect';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #043663 0%, #043663 100%);
    color: white;
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #032a4f 0%, #032a4f 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  ` : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover:not(:disabled) {
      background: #e5e7eb;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const MatchInfo = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const MatchInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MatchInfoLabel = styled.span`
  font-weight: 600;
  color: #374151;
`;

const MatchInfoValue = styled.span`
  color: #6b7280;
`;

const MatchDetailsModal = ({ isOpen, onClose, match, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    court_name: '',
    match_date: '',
    match_time: '',
    match_notes: ''
  });

  // Helper function to format date for input (YYYY-MM-DD)
  // Uses local date components to avoid timezone conversion issues
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse date string manually to avoid timezone issues
    let date;
    if (dateString instanceof Date) {
      // Use local date components, not UTC
      const year = dateString.getFullYear();
      const month = String(dateString.getMonth() + 1).padStart(2, '0');
      const day = String(dateString.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else if (typeof dateString === 'string') {
      // Extract YYYY-MM-DD from ISO string if present
      const match = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) {
        return match[1];
      }
      // Try parsing as date but use local components
      date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return '';
  };

  // Helper function to format time for input (HH:MM)
  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    // If it's already in HH:MM format, return as is
    if (/^\d{2}:\d{2}$/.test(timeString)) return timeString;
    // If it's a full datetime, extract time
    if (timeString.includes('T')) {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return '';
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return timeString;
  };

  // Pre-populate form with existing details when modal opens or match changes
  React.useEffect(() => {
    if (isOpen && match) {
      setFormData({
        court_name: match.courtDetails?.court_name || '',
        match_date: formatDateForInput(match.courtDetails?.match_date || match.match_date),
        match_time: formatTimeForInput(match.courtDetails?.match_time || match.match_time),
        match_notes: match.match_notes || ''
      });
    }
  }, [isOpen, match, match.courtDetails?.court_name, match.courtDetails?.match_date, match.courtDetails?.match_time, match.match_notes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const preservedDetails = match?.courtDetails?.match_details ?? match?.match_details ?? '';
    onSubmit({
      ...formData,
      match_details: preservedDetails != null ? String(preservedDetails) : '',
    });
  };

  // Check if there are any existing details (court_name, match_date, match_time, or match_notes)
  // Check both the original match data and current formData to handle dynamic updates
  const hasExistingDetails = () => {
    // First check if there are details in the original match data
    const hasOriginalDetails = !!(
      match.courtDetails?.court_name || 
      match.courtDetails?.match_date || 
      match.courtDetails?.match_time || 
      match.match_notes
    );
    
    // Also check current formData to handle when user is adding details
    const hasFormDetails = !!(
      formData.court_name?.trim() ||
      formData.match_date ||
      formData.match_time ||
      formData.match_notes?.trim()
    );
    
    return hasOriginalDetails || hasFormDetails;
  };

  const hasDetails = hasExistingDetails();

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {hasDetails ? 'View/Edit Match Details' : 'Submit Match Details'}
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <MatchInfo>
          <MatchInfoRow>
            <MatchInfoLabel>Match:</MatchInfoLabel>
            <MatchInfoValue>vs. {match.opponent}</MatchInfoValue>
          </MatchInfoRow>
          {match.courtDetails?.details_submitted_at && (
            <MatchInfoRow>
              <MatchInfoLabel>Last Updated:</MatchInfoLabel>
              <MatchInfoValue>
                {new Date(match.courtDetails.details_submitted_at).toLocaleDateString()} at{' '}
                {new Date(match.courtDetails.details_submitted_at).toLocaleTimeString()}
              </MatchInfoValue>
            </MatchInfoRow>
          )}
        </MatchInfo>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="court_name">Court/Club Name *</Label>
            <ClubSearchSelect
              id="court_name"
              key={isOpen ? `club-${match?.id || 'new'}` : 'club-closed'}
              value={formData.court_name}
              onChange={(name) =>
                setFormData((prev) => ({
                  ...prev,
                  court_name: name,
                }))
              }
              placeholder="Search and select a club…"
              required
            />
          </FormGroup>


          <FormGroup>
            <Label htmlFor="match_date">Match Date *</Label>
            <Input
              type="date"
              id="match_date"
              name="match_date"
              value={formData.match_date}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="match_time">Match Time *</Label>
            <Input
              type="time"
              id="match_time"
              name="match_time"
              value={formData.match_time}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="match_notes">Additional Notes</Label>
            <TextArea
              id="match_notes"
              name="match_notes"
              value={formData.match_notes}
              onChange={handleInputChange}
              placeholder="Any additional information about the venue or match"
            />
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading || !formData.court_name.trim() || !formData.match_date || !formData.match_time}
              style={!hasDetails ? {
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              } : {}}
              onMouseEnter={(e) => {
                if (!hasDetails && !loading && !e.target.disabled) {
                  e.target.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                }
              }}
              onMouseLeave={(e) => {
                if (!hasDetails && !loading && !e.target.disabled) {
                  e.target.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                }
              }}
            >
              {loading ? 'Saving...' : (hasDetails ? 'Update Details' : 'Add Details')}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MatchDetailsModal;
